import { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import axios from 'axios';
import Cookies from 'js-cookie';

import { SERVER } from './urls';

/*

Usar axiosGdt para apis internas al sitio. 

Resuelve CSRF + configuración por defecto necesaria ("withCredentials")

++++++
Explicado en:
https://en.wikipedia.org/wiki/Cross-site_request_forgery#Cookie-to-header_token

Convención de nombres (header, cookie):
https://angular.io/guide/http#security-xsrf-protection

+++++
Nota: En desarrollo podría no acceder fácilmente a una cookie de otro dominio 
(¿cors mal configurado?) . 
Por esa razón hago que el endpoint de login devuelva "data.csrfToken", y llamo a 
*setCurrentCsrfToken(response.data.csrfToken);*
No es lo ideal, un logout/login en otra solapa podría hacer lío con ese método.
*/

export const axiosGdt = axios.create({
  withCredentials: true,
  baseURL: SERVER,
});

export const axiosGdtWithoutCredentials = axios.create({
  withCredentials: false,
  baseURL: SERVER,
});

// //////////////////////////////////
// CSRF + INTENDED USER

// const enableIntendedUser = localStorage.getItem('enableIntendedUser') === 'true';
const enableIntendedUser = true;

axiosGdt.interceptors.request.use((req) => {
  req.headers.set('X-XSRF-TOKEN', getCurrentCsrfToken());
  if (enableIntendedUser && currentCsrfToken.intendedForUser) {
    req.headers.set('X-INTENDED-USER', currentCsrfToken.intendedForUser);
  }
  return req;
});

/**
 * currentCsrfToken
 * Sólo se usaría en desarrollo (fallback por cors del server mal configurado).
 * Si se está sirviendo sobre el mismo host => obtener desde la cookie "XSRF-TOKEN".
 *
 */
const currentCsrfToken: {
  value: string;
  intendedForUser?: number;
} = {
  value: '',
  intendedForUser: undefined,
};

export function getCurrentCsrfToken() {
  // get cookie: producción y test
  // get variable: desarrollo (fallback por server mal configurado)
  const cookieToken = Cookies.get('XSRF-TOKEN');
  if (cookieToken) {
    return cookieToken;
  }

  return currentCsrfToken.value;
}
/**
 * fallback, sólo necesario en desarrollo. Ver nota arriba.
 */
export function setCurrentCsrfToken(
  token: string,
  intendedForUser: number | undefined = undefined
) {
  currentCsrfToken.value = token;
  currentCsrfToken.intendedForUser = intendedForUser;
}

// //////////////////////////////////
/**
 * freshAccessToken
 * Lo mantengo en Authenticated, que es donde se renueva.
 */
let getAccessTokenSilentlyRef:
  | ((options?: GetTokenSilentlyOptions) => Promise<string>)
  | undefined;

export function setupAxiosTokenInterceptor(
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>
) {
  getAccessTokenSilentlyRef = getAccessTokenSilently;
}

export function removeAxiosTokenInterceptor() {
  getAccessTokenSilentlyRef = undefined;
}

async function getFreshAccessToken() {
  if (!getAccessTokenSilentlyRef) {
    return '';
  }
  const freshAccessToken = await getAccessTokenSilentlyRef({
    cacheMode: 'cache-only',
  });
  return freshAccessToken;
}

// localStorage.setItem('testPureStateless', 'true')
const testPureStateless = localStorage.getItem('testPureStateless') === 'true';
// localStorage.setItem('enableAccessTokenAuthorization', 'true')
const enableAccessTokenAuthorization =
  localStorage.getItem('enableAccessTokenAuthorization') !== 'false';

axiosGdt.interceptors.request.use(async (req) => {
  if (!enableAccessTokenAuthorization) {
    return req;
  }
  let token;
  try {
    token = await getFreshAccessToken();
  } catch (error) {
    console.error('Error getting fresh access token:', error);
  }

  if (token) {
    req.headers.set('Authorization', 'Bearer ' + token);
    if (testPureStateless) {
      // esto no debería funcionar. JSESSIONID debería ser httpOnly (permite el session fixation)
      document.cookie =
        'JSESSIONID=; expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      // await cookieStore.set('JSESSIONID', '123456.localmobile2');
      // if (URLs.SESSION_INVALIDATE_DBG !== req.url) {
      //  await sessionInvalidateJava();
      // }
    }
  }
  return req;
});
// //////////////////////////////////

export default axiosGdt;
